import {
  flexRender,
  type Row,
  type SortDirection,
} from '@tanstack/react-table';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  MdOutlineArrowDropDown,
  MdOutlineArrowDropUp,
} from '@teamworksdev/icons';

import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from '@mui/material';
import { DateRange } from '../dates/RangeCalendar';
import { cn } from '../utils';
import type { DataTableFilter, FilterOption } from './data-table-context';
import {
  type DataTableContextProps,
  DataTableProvider,
  useDataTableContext,
} from './data-table-context';
import {
  DefaultEmptySearchResultsPlaceholder,
  DefaultErrorPlaceholder,
  DefaultNoDataPlaceholder,
} from './data-table-placeholder';
import { DataTableRow } from './data-table-row';
import { DataTableFilters } from './dataTableFilters';
import { PaginationFooter } from './pagination';

const DataTable = <T,>() => {
  const {
    palette: { info, greyscale },
  } = useTheme();
  const {
    defaultFilterValues,
    controlledFilterState,
    filters,
    filtersEnabled,
    loading,
    paginationEnabled,
    searchEnabled,
    sortingEnabled,
    showError,
    showHeader = true,
    table,
    tableRef,
    title,
    isEditing,
    searchPlaceholder,
    getCommonPinningStyles,
    renderHeaderActions,
    EmptySearchResultsPlaceholder = DefaultEmptySearchResultsPlaceholder,
    ErrorPlaceholder = DefaultErrorPlaceholder,
    NoDataPlaceholder = DefaultNoDataPlaceholder,
    onFilterChange,
    onFilterStateChange,
    onResetFilters,
    onSearchChange,
    searchValue: controlledSearchValue,
  } = useDataTableContext();
  const [searchValue, setSearchValue] = useState('');

  // Use controlled search value if provided
  const currentSearchValue =
    controlledSearchValue !== undefined ? controlledSearchValue : searchValue;
  const handleSearchChange = useMemo(
    () =>
      controlledSearchValue !== undefined
        ? (value: string) => {
            onSearchChange?.(value);
          }
        : (value: string) => {
            setSearchValue(value);
            onSearchChange?.(value);
          },
    [controlledSearchValue, onSearchChange],
  );

  // Use controlled filter state if provided, otherwise use internal state
  const [internalFilterState, setInternalFilterState] = useState<
    Record<string, FilterOption[] | FilterOption>
  >(() => initializeFilters(filters, defaultFilterValues));

  const filterState =
    controlledFilterState !== undefined
      ? controlledFilterState
      : internalFilterState;
  const setFilterState =
    controlledFilterState !== undefined
      ? (
          newState:
            | Record<string, FilterOption[] | FilterOption>
            | ((
                prev: Record<string, FilterOption[] | FilterOption>,
              ) => Record<string, FilterOption[] | FilterOption>),
        ) => {
          const updatedState =
            typeof newState === 'function' ? newState(filterState) : newState;
          onFilterStateChange?.(updatedState);
        }
      : setInternalFilterState;

  const resetFilters = useCallback(() => {
    if (controlledFilterState !== undefined) {
      // Use updater function pattern for consistency
      const resetState = initializeFilters(filters, defaultFilterValues);
      onFilterStateChange?.(resetState);
    } else {
      setInternalFilterState(() =>
        initializeFilters(filters, defaultFilterValues),
      );
    }
  }, [
    filters,
    defaultFilterValues,
    controlledFilterState,
    onFilterStateChange,
  ]);

  // Wrapper for setSearchValue that supports functional updaters and delegates to handleSearchChange
  const handleSetSearchValue = useCallback(
    (valueOrUpdater: string | ((prev: string) => string)) => {
      const nextValue =
        typeof valueOrUpdater === 'function'
          ? valueOrUpdater(currentSearchValue)
          : valueOrUpdater;
      handleSearchChange(nextValue);
    },
    [currentSearchValue, handleSearchChange],
  );

  const showFilterRow = searchEnabled || (filtersEnabled && filters?.length);

  // Call onFilterChange when filterState changes
  useEffect(() => {
    if (!filtersEnabled) return;
    paginationEnabled && table.setPageIndex(0);
    onFilterChange?.(
      Object.entries(filterState).reduce((output, [filterId, state]) => {
        return {
          ...output,
          [filterId]:
            filterId !== 'dateRange'
              ? Array.isArray(state)
                ? state.map((option) => option.value)
                : []
              : state,
        };
      }, {}),
    );
  }, [filterState, filtersEnabled, onFilterChange, table, paginationEnabled]);

  const hasRowData = table.getFilteredRowModel().rows.length > 0;

  const renderRow = (row: Row<T>) => {
    return <DataTableRow row={row} key={row.id} />;
  };

  let placeholderContent: ReactNode = null;

  const isFilterApplied = Object.values(filterState).some((filters) =>
    Array.isArray(filters) ? filters.length > 0 : !!filters,
  );

  if (!loading) {
    if (showError) {
      placeholderContent = <ErrorPlaceholder />;
    } else if (!hasRowData && !currentSearchValue && !isFilterApplied) {
      placeholderContent = <NoDataPlaceholder />;
    } else if ((currentSearchValue || isFilterApplied) && !hasRowData) {
      placeholderContent = (
        <EmptySearchResultsPlaceholder
          clearSearch={() => {
            handleSearchChange('');
            resetFilters();
          }}
        />
      );
    }
  }

  return (
    <Paper
      variant="outlined"
      className="w-full h-full flex flex-col rounded-lg overflow-hidden border relative"
    >
      {showHeader && (
        <div
          className={cn(
            'flex items-center px-6 justify-between py-3',
            isEditing ? 'bg-black' : 'bg-gray-100',
          )}
        >
          {title && (
            <span
              className={cn('font-semibold', isEditing ? 'text-white' : '')}
            >
              {title}
            </span>
          )}
          {renderHeaderActions && renderHeaderActions()}
        </div>
      )}
      {showFilterRow && (
        <div
          className={cn(
            'flex items-center gap-2 min-h-14 h-14 overflow-x-auto overflow-y-hidden',
            // different padding depending on if the first filter is a search filter or something else
            !searchEnabled && 'pl-2',
          )}
        >
          {searchEnabled && (
            <div className="pl-4">
              <TextField
                sx={{ mt: 0, minWidth: '200px' }}
                value={currentSearchValue}
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                placeholder={searchPlaceholder}
                inputProps={{ 'aria-label': searchPlaceholder }}
              />
            </div>
          )}
          {filtersEnabled && (
            <DataTableFilters<T>
              filters={filters}
              filterState={filterState}
              setFilterState={setFilterState}
              setSearchValue={handleSetSearchValue}
              onResetFilters={() => {
                resetFilters();
                onResetFilters?.();
              }}
            />
          )}
        </div>
      )}
      {loading && (
        <div className="absolute top-[50%] left-[50%] h-full w-full bg-info-25 z-10 overflow-hidden">
          <CircularProgress
            variant="indeterminate"
            sx={{ color: info.main }}
            size={'3rem'}
          />
        </div>
      )}
      <TableContainer
        tabIndex={0}
        sx={{
          flexGrow: 1,
        }}
      >
        {placeholderContent || (
          <Table
            ref={tableRef}
            stickyHeader
            sx={{ '& th': { backgroundColor: greyscale[100] } }}
          >
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort =
                      sortingEnabled && header.column.getCanSort();
                    const sort = header.column.getIsSorted();

                    return (
                      <TableCell
                        className={cn(
                          'whitespace-nowrap transition-[padding] delay-200 pl-6',
                          {
                            'cursor-pointer': canSort,
                          },
                        )}
                        key={header.id}
                        id={`header-${header.id}`}
                        style={{
                          width: header.getSize(),
                          ...getCommonPinningStyles(header.column, true),
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div
                          className={cn(
                            'sticky flex flex-nowrap items-center gap-2',
                          )}
                        >
                          <div className="overflow-hidden overflow-ellipsis">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                          {canSort && <SortIndicator sort={sort} />}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {(paginationEnabled
                ? table.getPaginationRowModel()
                : table.getRowModel()
              ).rows.map(renderRow)}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {paginationEnabled && <PaginationFooter />}
    </Paper>
  );
};

export const DataTableWithContext = <T,>(props: DataTableContextProps<T>) => (
  <DataTableProvider<T> {...props}>
    <DataTable />
  </DataTableProvider>
);

type SortIndicatorProps = {
  sort: false | SortDirection;
};

const SortIndicator = ({ sort }: SortIndicatorProps) => {
  const {
    palette: { info, greyscaleLight },
  } = useTheme();

  if (sort === 'asc') {
    return <MdOutlineArrowDropUp size={24} color={info.main} />;
  }

  if (sort === 'desc') {
    return <MdOutlineArrowDropDown size={24} color={info.main} />;
  }

  return null;
};

const initializeFilters = (
  filters: DataTableFilter[] = [],
  defaultValues: Record<string, (string | number)[] | DateRange> = {},
) => {
  return Object.entries(defaultValues).reduce<
    Record<string, FilterOption[] | FilterOption>
  >((values, [filterId, filterValues]) => {
    const filter = filters?.find((f) => f.id === filterId);
    let value: FilterOption[] | FilterOption = [];
    if (filter?.type === 'select') {
      if (Array.isArray(filterValues)) {
        value =
          filter?.['options']?.filter((o: FilterOption) =>
            filterValues.includes(o.id),
          ) ?? [];
      } else {
        value = [];
      }
    } else if (filter?.type === 'dateRange') {
      value = {
        id: 'dateRange',
        value: filterValues,
      };
    }

    return {
      ...values,
      [filterId]: value,
    };
  }, {});
};
